import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getAdminUserDetail,
  adminRefundTicketSale,
} from "../../services/ingroove/adminService";
import { formatCurrency, formatDate, formatDateTime } from "../../utils/formatters";
import Avatar from "../../components/common/Avatar";
import Badge from "../../components/common/Badge";
import SectionPanel from "../../components/common/SectionPanel";
import {
  ArrowLeft,
  Wallet,
  Ticket,
  TrendingUp,
  RefreshCcw,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  X,
  CheckCircle2,
  Search,
} from "lucide-react";

const TX_PAGE_SIZE = 10;

const txBadgeVariant = (description) => {
  switch (description) {
    case "WalletTopUp": return "success";
    case "Refund": return "info";
    case "Payout": return "warning";
    case "PaymentGatewayFee": return "default";
    case "TicketSale": return "success";
    case "Commission": return "warning";
    default: return "default";
  }
};

const statusVariant = (status) => {
  switch (status) {
    case "Completed": return "success";
    case "Refunded": return "info";
    case "Pending": return "warning";
    case "Failed": return "danger";
    default: return "default";
  }
};

const RefundModal = ({ ticket, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
    <div className="bg-bg-surface border border-border rounded-2xl p-6 w-full max-w-md space-y-5">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-400/10 rounded-xl">
            <AlertTriangle size={16} className="text-amber-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Confirm Refund</h3>
            <p className="text-xs text-text-muted mt-0.5">{ticket.eventTitle}</p>
          </div>
        </div>
        <button onClick={onCancel} className="text-text-muted hover:text-text-primary transition">
          <X size={16} />
        </button>
      </div>

      <div className="bg-bg-elevated border border-border rounded-xl p-4 space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-text-muted">Ticket</span>
          <span className="text-text-primary font-medium">{ticket.ticketName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-text-muted">Paid</span>
          <span className="text-text-secondary">{formatCurrency(ticket.totalAmountPaid)}</span>
        </div>
        <div className="flex justify-between border-t border-border pt-2 mt-1">
          <span className="text-text-muted">Refund amount</span>
          <span className="text-primary font-semibold">{formatCurrency(ticket.refundableAmount)}</span>
        </div>
        <p className="text-xs text-text-hint pt-1">
          Banking fees are excluded from the refund.
        </p>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onCancel}
          className="flex-1 py-2 rounded-xl border border-border text-sm text-text-secondary hover:border-primary hover:text-primary transition"
        >
          Cancel
        </button>
        <button
          onClick={() => onConfirm(ticket.saleId)}
          disabled={loading}
          className="flex-1 py-2 rounded-xl bg-primary text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-50"
        >
          {loading ? "Processing..." : "Confirm Refund"}
        </button>
      </div>
    </div>
  </div>
);

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("transactions");

  // Transactions filter/pagination state
  const [txSearch, setTxSearch] = useState("");
  const [txTypeFilter, setTxTypeFilter] = useState("");
  const [txPage, setTxPage] = useState(1);

  // Refund state
  const [refundTarget, setRefundTarget] = useState(null);
  const [refunding, setRefunding] = useState(false);

  // Toast state
  const [toast, setToast] = useState(null);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const res = await getAdminUserDetail(id);
      setUser(res);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUser(); }, [id]);

  // Reset tx page when filters change
  useEffect(() => {
    setTxPage(1);
  }, [txSearch, txTypeFilter]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const handleRefund = async (ticketSaleId) => {
    setRefunding(true);
    try {
      const res = await adminRefundTicketSale(ticketSaleId);
      if (res.isSuccess) {
        showToast("success", "Refund processed successfully.");
        setRefundTarget(null);
        await fetchUser();
      } else {
        showToast("error", res.message ?? "Refund failed.");
      }
    } finally {
      setRefunding(false);
    }
  };

  if (loading)
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-6 bg-bg-surface rounded w-32" />
        <div className="bg-bg-surface border border-border rounded-2xl h-32" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-bg-surface border border-border rounded-xl h-20" />
          ))}
        </div>
        <div className="bg-bg-surface border border-border rounded-xl h-64" />
      </div>
    );

  if (!user)
    return <p className="text-text-muted text-sm">User not found.</p>;

  const wallet = user.wallet;
  const transactions = wallet?.transactions ?? [];
  const tickets = user.tickets ?? [];

  // Filtered transactions
  const filteredTx = transactions.filter((tx) => {
    const matchesSearch =
      !txSearch ||
      tx.name?.toLowerCase().includes(txSearch.toLowerCase()) ||
      tx.reference?.toLowerCase().includes(txSearch.toLowerCase());

    const matchesType =
      txTypeFilter === ""
        ? true
        : txTypeFilter === "Credit"
        ? tx.transactionType === "Credit"
        : tx.transactionType === "Debit";

    return matchesSearch && matchesType;
  });

  const txTotalPages = Math.ceil(filteredTx.length / TX_PAGE_SIZE);
  const pagedTx = filteredTx.slice(
    (txPage - 1) * TX_PAGE_SIZE,
    txPage * TX_PAGE_SIZE
  );

  const walletStats = [
    {
      label: "Balance",
      value: formatCurrency(wallet?.balance ?? 0),
      icon: <Wallet size={15} className="text-primary" />,
      accent: "bg-primary/10",
    },
    {
      label: "Total Top-ups",
      value: formatCurrency(wallet?.totalTopUps ?? 0),
      icon: <TrendingUp size={15} className="text-green-400" />,
      accent: "bg-green-400/10",
    },
    {
      label: "Total Spent",
      value: formatCurrency(wallet?.totalSpent ?? 0),
      icon: <Ticket size={15} className="text-purple-400" />,
      accent: "bg-purple-400/10",
    },
    {
      label: "Total Refunds",
      value: formatCurrency(wallet?.totalRefunds ?? 0),
      icon: <RefreshCcw size={15} className="text-amber-400" />,
      accent: "bg-amber-400/10",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-xl border text-sm shadow-lg transition-all
            ${toast.type === "success"
              ? "bg-bg-surface border-primary/30 text-primary"
              : "bg-bg-surface border-red-400/30 text-red-400"
            }`}
        >
          {toast.type === "success"
            ? <CheckCircle2 size={15} />
            : <AlertTriangle size={15} />}
          {toast.message}
        </div>
      )}

      {/* Back */}
      <button
        onClick={() => navigate("/ingroove/users")}
        className="flex items-center gap-1.5 text-xs text-text-muted hover:text-primary transition"
      >
        <ArrowLeft size={13} /> Back to Users
      </button>

      {/* Profile header */}
      <div className="bg-bg-surface border border-border rounded-2xl p-5 flex items-center gap-4">
        <Avatar name={user.name} surname={user.surname} image={user.image} size="lg" />
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-text-primary">
            {user.name} {user.surname}
          </h2>
          <p className="text-xs text-text-muted mt-0.5">{user.email}</p>
          {user.phoneNumber && (
            <p className="text-xs text-text-hint mt-0.5">{user.phoneNumber}</p>
          )}
        </div>
        <div className="text-right flex-shrink-0 space-y-1">
          <Badge
            label={user.isActive ? "Active" : "Inactive"}
            variant={user.isActive ? "success" : "default"}
          />
          <p className="text-xs text-text-hint">
            Joined {formatDate(user.joinedAt)}
          </p>
        </div>
      </div>

      {/* Wallet stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {walletStats.map((s) => (
          <div
            key={s.label}
            className="bg-bg-surface border border-border rounded-xl p-4 flex items-start gap-3"
          >
            <div className={`p-2 rounded-lg flex-shrink-0 ${s.accent}`}>{s.icon}</div>
            <div>
              <p className="text-xs text-text-muted">{s.label}</p>
              <p className="text-sm font-semibold text-text-primary mt-0.5">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-bg-surface border border-border rounded-xl p-1 w-fit">
        {[
          { key: "transactions", label: `Transactions (${transactions.length})` },
          { key: "tickets", label: `Tickets (${tickets.length})` },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition ${
              tab === t.key
                ? "ingroove-gradient-bg text-white shadow-glow"
                : "text-text-muted hover:text-text-secondary"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Transactions tab */}
      {tab === "transactions" && (
        <div className="bg-bg-surface border border-border rounded-xl overflow-hidden">
          {/* Header with search + filter */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Wallet size={14} className="text-primary" />
              <h2 className="text-sm font-semibold text-text-primary">
                Wallet Transactions
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Search */}
              <div className="relative">
                <Search
                  size={13}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
                />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={txSearch}
                  onChange={(e) => setTxSearch(e.target.value)}
                  className="pl-8 pr-8 py-1.5 text-xs bg-bg-base border border-border rounded-xl text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition w-44"
                />
                {txSearch && (
                  <button
                    onClick={() => setTxSearch("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-hint hover:text-text-muted"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>

              {/* Credit/Debit toggle */}
              <div className="flex gap-1 bg-bg-base border border-border rounded-xl p-1">
                {["", "Credit", "Debit"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setTxTypeFilter(f)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                      txTypeFilter === f
                        ? "ingroove-gradient-bg text-white shadow-glow"
                        : "text-text-muted hover:text-text-secondary"
                    }`}
                  >
                    {f === "" ? "All" : f}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Transaction rows */}
          {filteredTx.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <p className="text-xs text-text-muted">No transactions found</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {pagedTx.map((tx) => (
                <div
                  key={tx.id}
                  className="flex items-center justify-between px-5 py-3 hover:bg-bg-elevated transition"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        tx.transactionType === "Credit" ? "bg-green-400" : "bg-red-400"
                      }`}
                    />
                    <div className="min-w-0">
                      <p className="text-sm text-text-primary font-medium truncate">
                        {tx.name}
                      </p>
                      {tx.reference && (
                        <code className="text-xs text-text-hint block truncate">
                          {tx.reference}
                        </code>
                      )}
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge
                          label={tx.transactionDescription}
                          variant={txBadgeVariant(tx.transactionDescription)}
                        />
                        <span className="text-xs text-text-hint">
                          {formatDateTime(tx.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    <p
                      className={`text-sm font-semibold ${
                        tx.transactionType === "Credit" ? "text-green-400" : "text-red-400"
                      }`}
                    >
                      {tx.transactionType === "Credit" ? "+" : "-"}
                      {formatCurrency(Math.abs(tx.amount))}
                    </p>
                    <p className="text-xs text-text-hint mt-0.5">
                      Bal: {formatCurrency(tx.balance)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {txTotalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-border text-xs text-text-muted">
              <span>
                Page {txPage} of {txTotalPages} — {filteredTx.length} transactions
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setTxPage((p) => Math.max(1, p - 1))}
                  disabled={txPage === 1}
                  className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={13} />
                </button>
                <button
                  onClick={() => setTxPage((p) => Math.min(txTotalPages, p + 1))}
                  disabled={txPage === txTotalPages}
                  className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={13} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tickets tab */}
      {tab === "tickets" && (
        <SectionPanel
          icon={<Ticket size={14} className="text-purple-400" />}
          title="Ticket Purchases"
          empty={tickets.length === 0}
          emptyText="No tickets purchased yet"
        >
          <div className="divide-y divide-border">
            {tickets.map((ticket) => (
              <div
                key={ticket.saleId}
                className="flex items-center justify-between px-5 py-4 hover:bg-bg-elevated transition"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {ticket.eventImage ? (
                    <img
                      src={ticket.eventImage}
                      alt={ticket.eventTitle}
                      className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-bg-elevated flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm text-text-primary font-medium truncate">
                      {ticket.eventTitle}
                    </p>
                    <p className="text-xs text-text-muted mt-0.5">
                      {ticket.hubName} · {ticket.ticketName}
                    </p>
                    {ticket.extras.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {ticket.extras.map((ex, i) => (
                          <span
                            key={i}
                            className="text-xs bg-secondary/10 text-secondary border border-secondary/20 px-2 py-0.5 rounded-full"
                          >
                            {ex}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="text-right flex-shrink-0 ml-4 space-y-1.5">
                  <Badge
                    label={ticket.status}
                    variant={statusVariant(ticket.status)}
                  />
                  <p className="text-xs text-text-muted">
                    {formatDate(ticket.purchasedAt)}
                  </p>
                  <p className="text-xs text-text-hint">
                    Paid {formatCurrency(ticket.totalAmountPaid)}
                  </p>
                  {ticket.status === "Completed" && (
                    <button
                      onClick={() => setRefundTarget(ticket)}
                      className="text-xs text-amber-400 hover:opacity-80 transition font-medium block"
                    >
                      Refund {formatCurrency(ticket.refundableAmount)}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SectionPanel>
      )}

      {/* Refund modal */}
      {refundTarget && (
        <RefundModal
          ticket={refundTarget}
          onConfirm={handleRefund}
          onCancel={() => setRefundTarget(null)}
          loading={refunding}
        />
      )}
    </div>
  );
};

export default UserDetail;
